import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import {
  ActivatedRoute, Router,
} from '@angular/router';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { ChampionshipApiService } from '../../services/championship-api/ChampionshipApiService';
import { SaveChampionshipData } from '../../types/Event';

@Component({
  selector: 'app-championship-form-page',
  standalone: false,
  templateUrl: './championship-form-page.component.html',
  styleUrl: '../team-form-page/team-form-page.component.sass',
})
export class ChampionshipFormPageComponent implements OnInit {
  public championshipSlug: string | null = null;
  public mode: 'create' | 'edit' = 'create';
  public formData: SaveChampionshipData = {
    name: '',
    slug: null,
    logoUrl: null,
    websiteUrl: null,
    telegramUrl: null,
    vkUrl: null,
  };
  public selectedLogoFile: File | null = null;
  public selectedLogoFileName: string | null = null;
  public saving = false;
  public errorMessage: string | null = null;

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private championshipApi: ChampionshipApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    this.championshipSlug = this.route.snapshot.paramMap.get('championshipSlug');
    this.mode = this.championshipSlug ? 'edit' : 'create';
    try {
      if (this.championshipSlug) {
        await this.loadChampionship(this.championshipSlug);
      }
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Не удалось загрузить чемпионат.';
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public selectLogoFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.selectedLogoFile = file;
    this.selectedLogoFileName = file?.name ?? null;
  }

  public async saveChampionship(): Promise<void> {
    this.saving = true;
    this.errorMessage = null;

    try {
      const response = this.championshipSlug
        ? await this.championshipApi.updateChampionship(this.championshipSlug, this.formData)
        : await this.championshipApi.createChampionship(this.formData);
      const championship = this.unwrapResponse(response);
      if (!championship) {
        return;
      }

      if (this.selectedLogoFile) {
        const logoResponse = await this.championshipApi.uploadChampionshipLogo(championship.id, this.selectedLogoFile);
        if (!this.unwrapResponse(logoResponse)) {
          return;
        }
      }

      this.notificationService.notify('directory', this.mode === 'edit' ? 'Чемпионат обновлен' : 'Чемпионат добавлен', championship.name);
      await this.router.navigate([
        '/dashboard/championship',
        championship.slug ?? championship.id,
      ]);
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Не удалось создать чемпионат.';
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public get title(): string {
    return this.mode === 'edit' ? 'Редактирование чемпионата' : 'Новый чемпионат';
  }

  private async loadChampionship(championshipSlug: string): Promise<void> {
    const response = await this.championshipApi.getChampionship(championshipSlug);
    const championship = this.unwrapResponse(response);
    if (!championship) {
      this.errorMessage = 'Чемпионат не найден.';
      return;
    }

    this.formData = {
      name: championship.name,
      slug: championship.slug,
      logoUrl: championship.logoUrl,
      websiteUrl: championship.websiteUrl,
      telegramUrl: championship.telegramUrl,
      vkUrl: championship.vkUrl,
    };
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }
}
